import React, { useState, useEffect } from "react";
import { Plus, X, Loader2, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, authHeaders, useWarehousePriceHints, SOURCE_LABELS } from "./shared";

const today = () => new Date().toISOString().slice(0, 10);

export default function MovementForm({ clients, locations, onClose, onSave }) {
  const [form, setForm] = useState({ client_email: clients[0]?.email || "", movement_type: "ingresso", description: "", items: [{ name: "", quantity: 1, unit: "pz", description: "", weight_kg: "", dimensions: "" }], location_code: "", reference: "", cost: 0, note: "", entry_date: today(), exit_date: "" });
  const [saving, setSaving] = useState(false);
  const [costTouched, setCostTouched] = useState(false);
  const hints = useWarehousePriceHints(form.client_email);

  const tariffKey = form.movement_type === "ingresso" ? "in_cost_per_pallet" : "out_cost_per_pallet";
  const unitTariff = Number(hints?.tariff?.[tariffKey] || 0);
  const tariffSource = hints?.sources?.[tariffKey];
  const totalQty = form.items.reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
  const suggestedCost = Number((unitTariff * totalQty).toFixed(2));

  useEffect(() => {
    if (!costTouched && suggestedCost > 0) setForm(f => ({ ...f, cost: suggestedCost }));
  }, [suggestedCost, costTouched]);

  const setType = (t) => setForm(f => ({ ...f, movement_type: t, exit_date: t === "uscita" ? (f.exit_date || today()) : f.exit_date }));
  const days = form.entry_date
    ? Math.max(0, Math.round((new Date(form.exit_date || today()) - new Date(form.entry_date)) / 86400000))
    : null;

  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", quantity: 1, unit: "pz", description: "", weight_kg: "", dimensions: "" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    setForm({ ...form, items });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_email) return toast.error("Seleziona un cliente");
    if (!form.items.some(i => i.name && i.quantity > 0)) return toast.error("Aggiungi almeno un articolo");
    if (!form.entry_date) return toast.error("Inserisci la data di ingresso merce");
    if (form.exit_date && form.exit_date < form.entry_date) return toast.error("La data di uscita non puo precedere la data di ingresso");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/warehouse/movements`, { ...form, cost: parseFloat(form.cost) || 0 }, authHeaders());
      toast.success(form.movement_type === "ingresso" ? "Ingresso registrato" : "Uscita registrata");
      onClose();
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-8 overflow-y-auto">
      <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl m-4" data-testid="movement-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-heading font-bold text-lg uppercase tracking-wide">Nuovo Movimento</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Cliente *</label>
              <select value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} data-testid="mov-client"
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm">
                {clients.map(c => <option key={c.email} value={c.email}>{c.company || c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Tipo *</label>
              <div className="flex gap-2">
                {["ingresso", "uscita"].map(t => (
                  <button key={t} type="button" onClick={() => setType(t)} data-testid={`mov-type-${t}`}
                    className={`flex-1 py-2 rounded-sm text-sm font-bold uppercase ${form.movement_type === t ? (t === "ingresso" ? "bg-emerald-500 text-white" : "bg-red-500 text-white") : "bg-zinc-100 text-zinc-500"}`}>
                    {t === "ingresso" ? "Ingresso" : "Uscita"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 bg-zinc-50 border border-zinc-200 rounded-sm p-3">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Data ingresso merce *</label>
              <input type="date" value={form.entry_date} onChange={e => setForm({ ...form, entry_date: e.target.value })}
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" data-testid="mov-entry-date" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Data uscita merce</label>
              <input type="date" value={form.exit_date} min={form.entry_date || undefined} onChange={e => setForm({ ...form, exit_date: e.target.value })}
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" data-testid="mov-exit-date" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Giorni giacenza</label>
              <div className="h-[38px] flex items-center px-3 border border-dashed border-zinc-300 rounded-sm text-sm font-bold" data-testid="mov-storage-days">
                {days === null ? "-" : `${days} gg`}
                <span className="ml-2 text-[10px] font-normal uppercase text-zinc-400">{form.exit_date ? "chiusa" : "in corso"}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Posizione</label>
              <select value={form.location_code} onChange={e => setForm({ ...form, location_code: e.target.value })}
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm">
                <option value="">-- Nessuna --</option>
                {locations.map(l => <option key={l.code} value={l.code}>{l.code} - {l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Riferimento</label>
              <input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="DDT-001, ORD-123..."
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Costo (EUR)</label>
              <input type="number" step="0.01" min="0" value={form.cost} onChange={e => { setCostTouched(true); setForm({ ...form, cost: e.target.value }); }}
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" data-testid="mov-cost" />
              {unitTariff > 0 ? (
                <p className="text-[10px] text-zinc-500 mt-1" data-testid="mov-cost-hint">
                  {totalQty} x {unitTariff.toFixed(2)} € = <b>{suggestedCost.toFixed(2)} €</b>
                  <span className="ml-1 uppercase text-[9px] text-[#FF5200] font-bold">{SOURCE_LABELS[tariffSource] || tariffSource}</span>
                  {costTouched && (
                    <button type="button" onClick={() => { setCostTouched(false); setForm(f => ({ ...f, cost: suggestedCost })); }}
                      className="ml-1 underline text-blue-700">ripristina</button>
                  )}
                </p>
              ) : (
                <p className="text-[10px] text-zinc-400 mt-1">Nessuna tariffa handling nel listino cliente</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Descrizione</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Arrivo merce pallet elettronica..."
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase text-zinc-500">Articoli *</label>
              <button type="button" onClick={addItem} className="text-xs text-[#FF5200] font-bold flex items-center gap-1"><Plus size={14} /> Aggiungi</button>
            </div>
            <div className="space-y-2">
              {form.items.map((item, i) => (
                <div key={i} className="bg-zinc-50 p-2 rounded-sm space-y-2">
                  <div className="flex items-center gap-2">
                    <input value={item.name} onChange={e => updateItem(i, "name", e.target.value)} placeholder="Nome articolo"
                      className="flex-1 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm" data-testid={`item-name-${i}`} />
                    <input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, "quantity", parseInt(e.target.value) || 0)} placeholder="Qty"
                      className="w-20 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm text-center" data-testid={`item-qty-${i}`} />
                    <select value={item.unit} onChange={e => updateItem(i, "unit", e.target.value)}
                      className="w-20 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm">
                      <option value="pz">pz</option><option value="kg">kg</option><option value="plt">plt</option><option value="cll">cll</option><option value="box">box</option>
                    </select>
                    {form.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(i)} className="p-1 text-zinc-300 hover:text-red-500"><X size={16} /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" step="0.1" min="0" value={item.weight_kg ?? ""} onChange={e => updateItem(i, "weight_kg", e.target.value === "" ? "" : parseFloat(e.target.value))}
                      placeholder="Peso collo (kg)" className="border border-zinc-200 rounded-sm px-2 py-1.5 text-sm" data-testid={`item-weight-${i}`} />
                    <input value={item.dimensions || ""} onChange={e => updateItem(i, "dimensions", e.target.value)}
                      placeholder="Misure 120x80x150" className="border border-zinc-200 rounded-sm px-2 py-1.5 text-sm" data-testid={`item-dimensions-${i}`} />
                    <input value={item.description || ""} onChange={e => updateItem(i, "description", e.target.value)}
                      placeholder="Descrizione articolo" className="border border-zinc-200 rounded-sm px-2 py-1.5 text-sm" data-testid={`item-descr-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Note</label>
            <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Note aggiuntive..."
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm h-16 resize-none" />
          </div>

          <button type="submit" disabled={saving} data-testid="mov-save-btn"
            className="w-full bg-[#FF5200] text-white h-11 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : (form.movement_type === "ingresso" ? <ArrowDownToLine size={16} /> : <ArrowUpFromLine size={16} />)}
            Registra {form.movement_type === "ingresso" ? "Ingresso" : "Uscita"}
          </button>
        </form>
      </div>
    </div>
  );
}
